import { Injectable } from '@angular/core';
import { ConversationsService, CreateConversationDto } from '@careapp-group/ng-sdk-v2-new';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { RouterReducerState, SerializedRouterStateSnapshot } from '@ngrx/router-store';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import {
  catchError,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  tap,
  withLatestFrom,
} from 'rxjs/operators';
import { ChannelService, ChatClientService, StreamI18nService } from 'stream-chat-angular';
import { environment } from '../../../environments/environment';
import { FORCE_LOGOUT_401, Organisations, ResponseSuccessAuthLogoutAction } from '../actions';
import { getAuthenticationState } from '../auth/auth.selectors';
import { getConversationsState } from '../reducers/index';
import { Conversations } from './conversations.actions';
import { conversationsToken, userHasConversations } from './conversations.selectors';
import * as selectors from '../reducers/index';
import { Channel } from 'stream-chat';
import { MatDialog } from '@angular/material/dialog';
import { ParticipantSelectDialogData } from '../../organisation/create/participant-select-dialog/participant-select-dialog.component';
import { ConversationParticipantSelectDialogComponent } from '../../conversations/components/conversation-participant-select-dialog/conversation-participant-select-dialog.component';

@Injectable()
export class ConversationsEffects {
  // When a token is successfully retrieved, request to initiate the conversations service.
  initChatService = createEffect(() =>
    this.actions$.pipe(
      ofType(Conversations.requestInitConversations),
      withLatestFrom(
        this.store.select(getConversationsState),
        this.store.select(getAuthenticationState),
        this.store.select(userHasConversations)
      ),
      filter(
        ([action, conversationState, authState, userHasConversations]) =>
          userHasConversations === true &&
          conversationState.accessToken != null &&
          !conversationState.initiated
      ),
      switchMap(async ([action, conversationState, authState, userHasConversations]) => {
        try {
          await this.chatService.init(
            environment.streamApiKey,
            authState.auth.userId,
            conversationState.accessToken
          );
          await this.streamI18nService.setTranslation();
          await this.channelService.init(
            {
              members: { $in: [authState.auth.userId] },
            },
            null,
            null,
            false
          );

          return Conversations.responseSuccessInitConversations();
        } catch (err) {
          return Conversations.responseFailInitConversations(err);
        }
      })
    )
  );

  // Sets initial unread count for conversations on initialisation, listens for further unread events.
  listenForUnreadCount = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Conversations.responseSuccessInitConversations),
        tap((_) => {
          // Set initial unread message count
          const unreadMessageCount =
            (this.chatService?.chatClient?.user?.total_unread_count as number) ?? 0;
          if (unreadMessageCount > 0) {
            this.store.dispatch(
              Conversations.updateUnreadConversationCount({ count: unreadMessageCount })
            );
          }

          // Listen for unread messages.
          this.chatService.chatClient.on((event) => {
            if (event.total_unread_count !== undefined) {
              this.store.dispatch(
                Conversations.updateUnreadConversationCount({ count: event.total_unread_count })
              );
            }
          });
        })
      ),
    {
      dispatch: false,
    }
  );

  // When a token is successfully retrieved, request to initiate the conversations service.
  initChatServiceOnToken = createEffect(() =>
    this.actions$.pipe(
      ofType(Conversations.responseSuccessConversationsToken),
      map((_) => Conversations.requestInitConversations())
    )
  );

  // Don't leave a channel active when not on the conversations page.
  deselectChannelOnNavigateAway = createEffect(() =>
    this.store.select(selectors.getRouterState).pipe(
      filter((router) => !!router),
      map((router) => this.isOnConversationsPage(router)),
      distinctUntilChanged(),
      filter((isOnConversationsPage) => !isOnConversationsPage),
      switchMap(() => of(Conversations.requestDeselectConversation()))
    )
  );

  deselectChannel = createEffect(() =>
    this.actions$.pipe(
      ofType(Conversations.requestDeselectConversation),
      switchMap((action) => {
        try {
          this.channelService.deselectActiveChannel();
          return of(Conversations.responseSuccessDeselectConversation());
        } catch (err) {
          return of(Conversations.responseFailDeselectConversation(err));
        }
      })
    )
  );

  // Use to select a conversation where none is currently active.
  selectDefaultConversation(routerCID: string, channelCIDs: string[]) {
    // Select conversation from URL if exists.
    if (routerCID != null) {
      const index = channelCIDs.findIndex((cid) => cid === routerCID);
      if (index !== -1) {
        return of(Conversations.requestSelectConversation({ cid: channelCIDs[index] }));
      }
    }

    // Select first available conversation otherwise.
    return of(Conversations.requestSelectConversation({ cid: channelCIDs[0] }));
  }

  canSelectDefaultConversation(
    conversationsInitiated: boolean,
    activeChannel: Channel,
    channels: Channel[]
  ) {
    // Do not navigate if conversations have not been initialised
    if (!conversationsInitiated) {
      return false;
    }
    // Do not navigate if an active channel exists.
    if (activeChannel != null) {
      return false;
    }
    // Do not navigate if no channels exist to navigate to.
    if (channels?.length === 0 ?? true) {
      return false;
    }
    // Otherwise, active channel does not exist, and channels exist to navigate to.
    return true;
  }

  // On navigating to conversations, determine which channel should be selected.
  selectChannelOnConversationsEntry = createEffect(() =>
    this.store.select(selectors.getRouterState).pipe(
      // Only trigger this conversation selection when we route to the conversations page from another page.
      filter((routerState) => this.isOnConversationsPage(routerState)),
      // Do not retrigger when a new conversation is selected within the conversations page.
      distinctUntilChanged(),
      // Retrieve available channels, and any active channels
      withLatestFrom(
        this.channelService.channels$,
        this.channelService.activeChannel$,
        this.store.select(selectors.getConversationsState)
      ),
      filter(([router, channels, activeChannel, conversationState]) =>
        this.canSelectDefaultConversation(conversationState.initiated, activeChannel, channels)
      ),
      switchMap(([router, channels, activeChannel, conversationState]) => {
        return this.selectDefaultConversation(
          this.routerCID(router),
          channels.map((c) => c.cid)
        );
      })
    )
  );

  // If initialising conversations on the conversations page, select the first conversation.
  selectChannelOnInit = createEffect(() =>
    this.actions$.pipe(
      ofType(Conversations.responseSuccessInitConversations),
      withLatestFrom(
        this.store.select(selectors.getRouterState),
        this.channelService.channels$,
        this.store.select(selectors.getConversationsState),
        this.channelService.activeChannel$
      ),
      filter(
        ([action, routerState, channels, conversationState, activeChannel]) =>
          this.isOnConversationsPage(routerState) &&
          this.canSelectDefaultConversation(conversationState.initiated, activeChannel, channels)
      ),
      switchMap(([_, router, channels]) => {
        return this.selectDefaultConversation(
          this.routerCID(router as any),
          (channels as Channel[]).map((c) => c.cid)
        );
      })
    )
  );

  showCreateConversationDialogOnRoute = createEffect(() =>
    this.store.select(selectors.getRouterState).pipe(
      filter((router) => !!router),
      map((router) => this.routerCreateFlag(router)),
      distinctUntilChanged(),
      filter((routerCreateFlag) => !!routerCreateFlag),
      switchMap(() => of(Conversations.requestCreateConversationDialog()))
    )
  );

  showCreateConversationDialog = createEffect(
    () =>
      this.actions$.pipe(
        ofType(Conversations.requestCreateConversationDialog),
        switchMap(async (action) => {
          const dialogRef = this.dialog.open(ConversationParticipantSelectDialogComponent, {
            width: '60%',
            data: <ParticipantSelectDialogData>{
              initialSelection: [],
            },
          });
          const selectedIds: string[] = await dialogRef.afterClosed().toPromise();
          if (selectedIds && selectedIds.length >= 0) {
            this.store.dispatch(Conversations.requestStartConversation({ memberIds: selectedIds }));
          }
        })
      ),
    { dispatch: false }
  );

  // When a conversation is created, select that conversation.
  selectConversationOnCreation = createEffect(() =>
    this.actions$.pipe(
      ofType(Conversations.responseSuccessStartConversation),
      map((action) => Conversations.requestSelectConversation({ cid: action.cid }))
    )
  );

  retrieveToken = createEffect(() =>
    this.actions$.pipe(
      ofType(Conversations.requestConversationsToken),
      withLatestFrom(this.store.select(conversationsToken)),
      filter(([action, existingToken]) => existingToken == null),
      switchMap(([action, existingToken]) => {
        return this.conversationsService.generateAccessToken().pipe(
          map((res) => {
            return Conversations.responseSuccessConversationsToken(res);
          }),
          catchError((res) => of(Conversations.responseFailConversationsToken(res)))
        );
      })
    )
  );

  // When an org config response arrives where conversations is enabled, retrieve a
  // conversations access token if none already exists.
  retrieveTokenOnConfig = createEffect(() =>
    this.actions$.pipe(
      ofType(Organisations.RESPONSE_SUCCESS_ORG_CONFIG),
      withLatestFrom(this.store.select(getConversationsState)),
      filter(
        ([action, conversationsState]) =>
          (conversationsState?.accessToken == null ?? true) && action.payload.conversationsEnabled
      ),
      map((_) => Conversations.requestConversationsToken())
    )
  );

  // When conversation participants have been selected.
  startConversation = createEffect(() =>
    this.actions$.pipe(
      ofType(Conversations.requestStartConversation),
      withLatestFrom(this.store.select(getAuthenticationState)),
      filter(([action, authState]) => action.memberIds != null),
      switchMap(([action, authState]) => {
        return this.conversationsService
          .createConversation({
            createConversationDto: {
              memberIds: [...action.memberIds, authState.auth.userId],
            } as CreateConversationDto,
          })
          .pipe(
            map((res) => Conversations.responseSuccessStartConversation({ cid: res.cid })),
            catchError((res) => of(Conversations.responseFailStartConversation(res)))
          );
      })
    )
  );

  // When new conversation participants have been selected.
  addMembersToConversation = createEffect(() =>
    this.actions$.pipe(
      ofType(Conversations.requestAddToChannel),
      withLatestFrom(this.store.select(getAuthenticationState)),
      filter(([action, authState]) => action.memberIds != null),
      switchMap(([action, authState]) => {
        return this.conversationsService
          .addConversationMembers({
            id: action.channelId,
            createConversationDto: {
              memberIds: [...action.memberIds],
            } as CreateConversationDto,
          })
          .pipe(
            map((res) =>
              Conversations.responseSuccessAddToChannel({
                channelId: res.cid,
                memberIds: res.memberIds,
              })
            ),
            catchError((res) => of(Conversations.responseFailAddToChannel(res)))
          );
      })
    )
  );

  selectConversation = createEffect(() =>
    this.actions$.pipe(
      ofType(Conversations.requestSelectConversation),
      withLatestFrom(this.store.select(getConversationsState), this.channelService.channels$),
      filter(
        ([action, conversationState, channels]) =>
          action.cid != null &&
          conversationState.initiated === true &&
          conversationState.accessToken != null
      ),
      switchMap(async ([action, conversationState, channels]) => {
        try {
          await this.channelService.loadMoreChannels();
          const channel = channels.find((channel) => channel.cid === action.cid);
          if (channel != null) {
            await this.channelService.setAsActiveChannel(channel);
            return Conversations.responseSuccessSelectConversation({ cid: action.cid });
          } else {
            return Conversations.responseFailSelectConversation({
              error: 'Error routing to conversation: Cannot find channel',
            });
          }
        } catch (err) {
          return Conversations.responseFailSelectConversation(err);
        }
      })
    )
  );

  // When a conversation has been selected when open, listen for the event and tell the router.
  markConversationAsSelected = createEffect(() =>
    this.channelService.activeChannel$.pipe(
      filter((activeChannel) => !!activeChannel?.cid),
      map((activeChannel) =>
        Conversations.responseSuccessSelectConversation({ cid: activeChannel.cid })
      )
    )
  );

  listConversationMembers = createEffect(() =>
    this.actions$.pipe(
      ofType(Conversations.requestConversationMembers),
      switchMap((action) => {
        return this.conversationsService.getConversationsMembers().pipe(
          map((res) => Conversations.responseSuccessConversationMembers({ members: res })),
          catchError((res) => of(Conversations.responseFailConversationMembers(res)))
        );
      })
    )
  );

  addChannelModerators = createEffect(() =>
    this.actions$.pipe(
      ofType(Conversations.requestAddChannelModerators),
      switchMap((action) => {
        return this.conversationsService
          .addConversationModerators({
            id: action.channelId,
            createConversationDto: {
              memberIds: action.ids,
            } as CreateConversationDto,
          })
          .pipe(
            map((res) =>
              Conversations.responseSuccessAddChannelModerators({
                channelId: action.channelId,
                ids: action.ids,
              })
            ),
            catchError((res) => of(Conversations.responseFailAddChannelModerators(res)))
          );
      })
    )
  );

  demoteChannelModerator = createEffect(() =>
    this.actions$.pipe(
      ofType(Conversations.requestDemoteChannelModerator),
      switchMap((action) => {
        return this.conversationsService
          .removeConversationModerator({ id: action.channelId, moderatorId: action.moderatorId })
          .pipe(
            map((res) =>
              Conversations.responseSuccessDemoteChannelModerator({
                channelId: action.channelId,
                moderatorId: action.moderatorId,
              })
            ),
            catchError((res) => of(Conversations.responseFailDemoteChannelModerator(res)))
          );
      })
    )
  );

  handleLogout = createEffect(() =>
    this.actions$.pipe(
      ofType(
        ResponseSuccessAuthLogoutAction,
        FORCE_LOGOUT_401,
        Conversations.requestDisconnectConversations
      ),
      switchMap(async (action) => {
        try {
          await this.chatService.disconnectUser();
          return Conversations.responseSuccessDisconnectConversations();
        } catch (err) {
          return Conversations.responseFailDisconnectConversation(err);
        }
      })
    )
  );

  private isOnConversationsPage(router: RouterReducerState<SerializedRouterStateSnapshot>) {
    return router && router.state ? conversationsRegex.test(router.state.url) : false;
  }

  // Return the conversation ID, if existent in the URL query parameters.
  private routerCID(router: RouterReducerState<SerializedRouterStateSnapshot>) {
    if (this.isOnConversationsPage(router)) {
      const params: { cid: string } = (router?.state as any).queryParams ?? {};
      if (params.hasOwnProperty('cid') && !!params.cid.length) {
        return params.cid;
      }
    }
  }

  // Return if a new conversation should be created.
  private routerCreateFlag(router: RouterReducerState<SerializedRouterStateSnapshot>) {
    if (this.isOnConversationsPage(router)) {
      const params: { create?: string } = (router?.state as any).queryParams ?? {};
      if (params.hasOwnProperty('create') && !!params.create) {
        return params.create;
      }
    }
  }

  constructor(
    private actions$: Actions<any>,
    private store: Store<any>,
    private conversationsService: ConversationsService,
    private chatService: ChatClientService,
    private channelService: ChannelService,
    private streamI18nService: StreamI18nService,
    private dialog: MatDialog
  ) {}
}

export const conversationsRegex: RegExp = /^(\/conversations)/;
